import { TFunction } from 'i18next'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { LOCIZE_NAMESPACE } from 'main/common'
import DetailedPricingModal from 'main/components/lead/DetailedPricingModal'
import { NAV_MODALS } from 'main/hooks/useSSOContext'
import { SchoolPage_school } from 'main/pages/School/gql'
import { i18nKeyPrefix } from 'main/pages/School/i18n'
import { tuitionPriceRange } from 'main/pages/School/Pricing/util'
import { Hidden, Visible } from 'main/ui'
import { formatCurrency } from 'main/util/currency'
import { IPricingRange } from 'main/util/profile'

import useNavContextModal from '../common/useNavContextModal'
import {
  BulletContainer,
  DetailedPricingNativeLink,
  PricingContainer,
  PricingText,
} from './CTABarFullPricing.styles'

const PRE = `${i18nKeyPrefix}-CTA-FULL-PRICING`

interface IProps extends RouteComponentProps {
  school: SchoolPage_school
}

function CTABarFullPricing(props: IProps) {
  const { school, location } = props
  const {
    tuitionBillingPolicy: { weeklyBillingAccepted },
  } = school

  const { t } = useTranslation(LOCIZE_NAMESPACE)

  const [isModalOpen, openModal, closeModal] = useNavContextModal(
    NAV_MODALS.FULL_PRICING,
    location.pathname,
  )

  const priceRange = tuitionPriceRange(school)
  if (!priceRange) return null

  return (
    <>
      <PricingContainer>
        <Visible lg xl>
          <PricingText>{renderPricingRange(priceRange, weeklyBillingAccepted, t)}</PricingText>
          <BulletContainer>&bull;</BulletContainer>
          <DetailedPricingNativeLink
            data-pendo="school-page-detailed-pricing-link"
            onClick={() => openModal()}
          >
            {t(`${PRE}-DETAILED`)}
          </DetailedPricingNativeLink>
        </Visible>

        <Hidden lg xl>
          <DetailedPricingNativeLink
            data-pendo="school-page-detailed-pricing-link"
            onClick={() => openModal()}
          >
            {renderPricingRange(priceRange, weeklyBillingAccepted, t)}
          </DetailedPricingNativeLink>
        </Hidden>
      </PricingContainer>
      {isModalOpen && (
        <DetailedPricingModal schoolId={school.id} onRequestClose={() => closeModal()} />
      )}
    </>
  )
}

function renderPricingRange(
  priceRange: IPricingRange,
  weeklyBillingAccepted: boolean,
  t: TFunction,
) {
  const { min, max, weeklyMin, weeklyMax } = priceRange
  const formattedMin = formatCurrency(min)
  const formattedMax = formatCurrency(max)

  const visibleWeeklyPricing = weeklyMax !== null && weeklyMin !== null

  if (weeklyBillingAccepted && visibleWeeklyPricing) {
    const formattedWeeklyMin = formatCurrency(weeklyMin!)
    const formattedWeeklyMax = formatCurrency(weeklyMax!)
    return t(`${PRE}-MONTHLY-AND-WEEKLY`, {
      formattedMin,
      formattedMax,
      formattedWeeklyMin,
      formattedWeeklyMax,
    })
  }

  return t(`${PRE}-MONTHLY`, { formattedMin, formattedMax })
}

export default withRouter(CTABarFullPricing)
